/**
 * O QUE UMA FATURA É: gerada por assinatura, consolidada do cliente, parcela de
 * grupo ou avulsa.
 *
 * ⛔ O DEFEITO QUE ABRIU ISTO (02/10/2026). "Gerar Faturas em Lote" tratava toda
 * fatura sem `contractId` como CONSOLIDADA e escondia o cliente inteiro da
 * competência. Mas avulsa também nasce sem `contractId`: três retroativos de
 * reajuste lançados no fim de setembro com referência 2026-10 (IFRN
 * FAT202609144, SORRISO FAT202609146, COM8DN FAT202609147) esconderam três
 * assinaturas ativas que precisavam faturar, R$ 10.343,67 de MRR.
 *
 * ⛔ FONTE ÚNICA. É a única função da tela que decide isso. Hoje ela ADIVINHA
 * pelos campos; quando o backend passar a gravar o tipo na fatura, troca-se a
 * primeira linha de `tipoDaFatura` para ler o campo, e só.
 *
 * A REGRA DE HOJE, para fatura sem `contractId` e sem `invoiceGroupId`:
 * consolidada é a que o backend gera para cobrir a COMPETÊNCIA
 * (`createConsolidatedFromClient`), e o período dela começa no mês da
 * referência ou no mês anterior (órgão público e ciclo customizado cobram o mês
 * anterior). Avulsa (retroativo, ajuste) tem período fora dessa janela, ou não
 * tem período. Medido em produção (02/10/2026), as 372 faturas sem contrato:
 * 367 começam no mês da referência; as 5 que começam fora são todas avulsas de
 * retroativo (clientes 25, 27, 30, 35 e 125); nenhuma consolidada começa fora.
 *
 * ⚠️ O erro que esta regra pode cometer é tomar avulsa por consolidada, se uma
 * avulsa for lançada com o período da própria competência. Aí o cliente some do
 * lote, que é o comportamento de antes, e não uma duplicata. O erro inverso,
 * mostrar a consolidada da FESTVAL como pendente, geraria uma segunda fatura de
 * R$ 21 mil, e é o que a regra protege.
 */

export type TipoDaFatura = 'assinatura' | 'consolidada' | 'parcela_de_grupo' | 'avulsa';

export interface FaturaParaTipo {
  contractId?: number | null;
  invoiceGroupId?: number | null;
  referenceMonth?: string | null;
  periodStart?: string | null;
}

/** 'YYYY-MM' do mês anterior. */
function mesAnterior(ym: string): string {
  const [a, m] = ym.split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}

export function tipoDaFatura(inv: FaturaParaTipo): TipoDaFatura {
  if (inv.contractId) return 'assinatura';
  if (inv.invoiceGroupId) return 'parcela_de_grupo';
  const comp = String(inv.referenceMonth || '').slice(0, 7);
  // Mês civil fatiado da string: período é `date`, não instante.
  const inicio = String(inv.periodStart || '').slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(comp) && /^\d{4}-\d{2}$/.test(inicio) && (inicio === comp || inicio === mesAnterior(comp))) {
    return 'consolidada';
  }
  return 'avulsa';
}

/** A fatura cobre a competência do cliente inteiro (esconde as assinaturas dele do lote)? */
export const cobreOClienteNaCompetencia = (inv: FaturaParaTipo) => tipoDaFatura(inv) === 'consolidada';
